import { useEffect, useMemo, useState } from 'react'
import {
  Spin,
  useGlobalState,
  useLocalStorage,
  useTopbarComponent,
  useContentRefreshDomains,
} from '@pepper/ui'

import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { ListType, QueryParamFilters } from './types'

import PropertySelectorModal from 'components/PropertySelectorModal'
import { useRoi } from '../hooks/useRoi'
import { PENDING_STATES } from './constants'

import Filters from './Filters/Filters'
import InfoCard from './InfoCard/InfoCard'
import StatGraph from './StatGraph/StatGraph'
import TopOrganicPages from './TopOrganicPages'
import TopOrganicKeywords from './TopOrganicKeywords'
import GeneratingState from './GeneratingState'
import ErrorState from './ErrorState'
import EmptyState from './EmptyState'

import s from './ContentRoi.module.css'

const ContentRoi = () => {
  const companyId = useSelectedCompanyId()
  const { domainId } = useGlobalState()
  const [viewFullPage, setViewFullPage] = useState<ListType>()
  const [showNonBranded, setShowNonBranded] = useState(true)
  const [lsDomainId] = useLocalStorage('domain_id', '')
  const [queryParams, setQueryParams] = useQueryParams<QueryParamFilters>({
    period: withDefault(StringParam, undefined),
    startDate: withDefault(StringParam, undefined),
    device: withDefault(StringParam, undefined),
    page: withDefault(NumberParam, 1),
  })

  const {
    data: domains,
    isLoading: isDomainsLoading,
    isGoogleConnected,
    connectedGoogleAccount,
    refetch,
  } = useContentRefreshDomains(companyId)

  const selectedDomainId = (domainId ?? lsDomainId) || domains[0]?.id

  const responses = useRoi({
    companyId,
    domainId: selectedDomainId,
    date: queryParams.startDate,
    period: queryParams.period,
    deviceCategory: queryParams.device,
    ...(showNonBranded && { queryType: 'non_branded' }),
    isEnabled: !!(
      queryParams.period &&
      queryParams.startDate &&
      selectedDomainId
    ),
  })

  const [
    spendsResponse,
    statsResponse,
    pagesDistributionResponse,
    keywordsDistributionResponse,
  ] = responses

  const selectedDomain = domains?.find(d => d.id === selectedDomainId)

  const isGaGscConnected =
    !!selectedDomain?.gsc_domain_property && !!selectedDomain?.ga_property

  const title = useMemo(() => {
    return (
      <div className={s.headerWrapper}>
        <span>Analytics & ROI</span>
      </div>
    )
  }, [])

  const isPendingState = () =>
    responses.some(r => PENDING_STATES.includes(r.data?.report_status))

  const isErrorState = () =>
    responses.some(r => r.isError) ||
    responses.some(r => r.data?.report_status === 'error')

  useTopbarComponent({
    header: title,
  })

  const handleViewMore = (listType: ListType) => {
    setViewFullPage(listType)
  }

  useEffect(() => {
    setQueryParams({
      page: 1,
    })
  }, [selectedDomainId])

  if (!isDomainsLoading && (!isGaGscConnected || !isGoogleConnected)) {
    return (
      <div className={s.wrapper}>
        <Filters companyId={companyId} />
        {!!connectedGoogleAccount && (
          <PropertySelectorModal
            domains={domains}
            connectedAccount={connectedGoogleAccount}
            companyId={companyId}
            onSuccess={refetch}
            onDisconnect={refetch}
          />
        )}
        <EmptyState
          isGoogleAccountConnected={isGoogleConnected}
          connectedUser={connectedGoogleAccount}
        />
      </div>
    )
  }

  if (isErrorState()) {
    return (
      <div className={s.wrapper}>
        <Filters companyId={companyId} />
        <ErrorState />
      </div>
    )
  }

  if (isPendingState()) {
    return (
      <div className={s.wrapper}>
        <Filters companyId={companyId} />
        <GeneratingState />
      </div>
    )
  }

  return (
    <div className={s.wrapper}>
      <Filters companyId={companyId} />
      {isDomainsLoading && (
        <div className={s.body}>
          <Spin />
        </div>
      )}
      {isGaGscConnected && !isDomainsLoading && (
        <div className={s.body}>
          {!viewFullPage && (
            <>
              {spendsResponse.data?.data?.traffic && (
                <InfoCard traffic={spendsResponse.data.data.traffic} />
              )}
              {statsResponse.data?.data && (
                <StatGraph
                  period={queryParams.period}
                  data={statsResponse.data?.data}
                  isLoading={statsResponse.isLoading}
                />
              )}
            </>
          )}

          {viewFullPage !== 'KEYWORDS' && (
            <TopOrganicPages
              domainId={selectedDomainId}
              handleViewMore={handleViewMore}
              viewFullPage={viewFullPage === 'PAGES'}
              date={queryParams.startDate}
              period={queryParams.period}
              deviceCategory={queryParams.device}
              distributionResponse={pagesDistributionResponse}
            />
          )}
          {viewFullPage !== 'PAGES' && (
            <TopOrganicKeywords
              domainId={selectedDomainId}
              handleViewMore={handleViewMore}
              viewFullPage={viewFullPage === 'KEYWORDS'}
              date={queryParams.startDate}
              period={queryParams.period}
              deviceCategory={queryParams.device}
              handleToggle={() => setShowNonBranded(!showNonBranded)}
              showNonBranded={showNonBranded}
              distributionResponse={keywordsDistributionResponse}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default ContentRoi
